'use client'

import React, { useContext, useState } from 'react'
import { SoloViewerToken } from '@utils/common'
import { isOob } from './token-solo-viewer'
import { TrackedButtonNames } from '@utils/analytics'
import { trackButtonClicked } from '@utils/analytics'

export type SoloViewerData = {
  token?: SoloViewerToken
  tokenList?: SoloViewerToken[]
  image?: string
  imageList?: string[]
}

export function useSoloViewer(): SoloViewerContextType {
  const context = useContext(SoloViewerContext)
  if (context === undefined) {
    throw new Error('useSoloViewer must be used within a SoloViewerProvider')
  }
  return context
}

type SoloViewerContextType = {
  isSoloViewerOpen: boolean
  setSoloViewerData: (data: { token?: SoloViewerToken; tokenList?: SoloViewerToken[] }) => void
  setSoloViewerImageData: (data: { image?: string; imageList?: string[] }) => void
  soloViewerData: SoloViewerData
}

export const SOLO_VIEWER_KEY = 'token'

export const SoloViewerContext = React.createContext<SoloViewerContextType>({
  isSoloViewerOpen: false,
  setSoloViewerData: () => undefined,
  setSoloViewerImageData: () => undefined,
  soloViewerData: {}
})

export const SoloViewerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [soloViewerData, setSoloViewerData] = useState<SoloViewerData>({
    token: undefined,
    tokenList: undefined,
    image: undefined,
    imageList: undefined
  })

  const openSoloView = ({ token, tokenList }: { token?: SoloViewerToken; tokenList?: SoloViewerToken[] }) => {
    setSoloViewerData({ token, tokenList, image: undefined, imageList: undefined })
    trackButtonClicked(TrackedButtonNames.openSoloViewer, {
      token: token && !isOob(token) ? token : undefined,
      oobToken: token && isOob(token) ? token : undefined
    })
  }

  const openImageSoloView = ({ image, imageList }: { image?: string; imageList?: string[] }) => {
    setSoloViewerData({ token: undefined, tokenList: undefined, image, imageList })
    trackButtonClicked(TrackedButtonNames.openSoloViewer, {
      customProperties: {
        image: image ? image : undefined,
        'Image list': imageList ? imageList.join(', ') : undefined
      }
    })
  }

  return (
    <SoloViewerContext.Provider
      value={{
        isSoloViewerOpen: !!soloViewerData.token,
        setSoloViewerData: openSoloView,
        setSoloViewerImageData: openImageSoloView,
        soloViewerData
      }}
    >
      {children}
    </SoloViewerContext.Provider>
  )
}
